/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useOutboundRouteList.ts
 🗓️ Created: 2025-01-17
 🧩 Description:
    Custom hook for managing Outbound Routes list.
    Integrates with useDataGrid for pagination, sorting, and search.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useContextStore } from '../../../store/context';
import { fetchOutboundRoutes } from '../api/outboundRouteApi';
import { OutboundRoute } from '../types';

interface UseOutboundRouteListOptions {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Hook for managing Outbound Routes list
 * Implements Domain Contextual Filtering pattern
 */
export function useOutboundRouteList(options: UseOutboundRouteListOptions = {}) {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = options.domainId ?? selectedDomain?.id;

  const fetchRoutes = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchOutboundRoutes(
          {
            page: params.page,
            limit: params.limit,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
            domainId: effectiveDomainId
          },
          params.search,
          params.sortBy
        );

        return {
          data: result.data as OutboundRoute[],
          pagination: result.pagination
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : t('outboundRoutes.messages.loadError');
        showError(message);
        return {
          data: [] as OutboundRoute[],
          pagination: {
            page: params.page,
            limit: params.limit,
            total: 0,
            totalPages: 0
          }
        };
      }
    },
    [showError, t, effectiveDomainId]
  );

  const availableColumns = useMemo(
    () => [
      {
        key: 'name',
        label: t('outboundRoutes.routeName'),
        visible: true,
        sortable: true
      },
      {
        key: 'routingStrategy',
        label: t('outboundRoutes.routingStrategy'),
        visible: true,
        sortable: false
      },
      {
        key: 'patterns',
        label: t('outboundRoutes.patternCount'),
        visible: true,
        sortable: false
      },
      {
        key: 'gateways',
        label: t('outboundRoutes.gatewayCount'),
        visible: true,
        sortable: false
      },
      {
        key: 'createdAt',
        label: t('outboundRoutes.createdAt'),
        visible: false,
        sortable: true
      },
      {
        key: 'actions',
        label: t('common.actions'),
        visible: true,
        sortable: false
      }
    ],
    [t]
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData: fetchRoutes,
    initialColumns: availableColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'outbound-routes',
    reloadKey: effectiveDomainId ?? null
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && options.onRefreshNeeded) {
      options.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    data: data as OutboundRoute[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId
  };
}

